import type { Block, CollectionSummary, Thought, Writing } from '../types'
import { countWords } from '../text'
import demoPhoto from '../../assets/reflection-photo.jpeg'

// Sample content from the design handoff, so the local demo backend opens on
// the same writings and thoughts the mockups show. Timestamps are relative to
// "now" so today's reflection always has today's thoughts.

export const DEMO_PHOTO_PATH = 'demo/reflection-photo.jpeg'
export const DEMO_PHOTO_URL: string = demoPhoto
/**
 * Demo recordings have no audio file: `demo:` sources play as a silent,
 * timer-driven clock (see lib/useAudioPlayer.ts) so the prototype can show
 * narration sync and voice memos offline.
 */
export const DEMO_NARRATION_SRC = 'demo:narration'
export const DEMO_VOICE_SRC = 'demo:voice'

function at(now: Date, daysAgo: number, time: string): string {
  const [hm, ampm] = time.split(' ')
  const [h, m] = hm.split(':').map(Number)
  const d = new Date(now)
  d.setDate(d.getDate() - daysAgo)
  d.setHours((h % 12) + (ampm === 'pm' ? 12 : 0), m, 0, 0)
  // Keep today's thoughts in the past even early in the morning.
  if (daysAgo === 0 && d > now) d.setTime(now.getTime() - (24 - d.getHours()) * 60_000)
  return d.toISOString()
}

interface DemoThought {
  id: string
  days: number
  time: string
  body: string
  title?: string
  source_title?: string
  source_url?: string
  photo?: boolean
}

const THOUGHTS: DemoThought[] = [
  { id: 't1', days: 0, time: '8:14 am', body: 'Attention is the rarest and purest form of generosity.', source_title: 'Simone Weil, quoted in "On paying attention"', source_url: 'https://www.theparisreview.org/blog/on-paying-attention' },
  { id: 't2', days: 0, time: '9:40 am', body: 'Ask Maya whether the intro can be half as long.' },
  { id: 't3', days: 0, time: '11:52 am', body: "Why do I keep the tab open for the thing I'm not doing? It's like leaving the light on in a room I've walked out of." },
  { id: 't4', days: 0, time: '1:26 pm', body: '"Chapter four, still"', photo: true },
  { id: 't5', days: 0, time: '3:07 pm', body: 'Most of what we call busyness is the avoidance of one difficult thing, spread thinly across a whole day.', source_title: 'Oliver Burkeman, The Imperfectionist', source_url: 'https://ckarchive.com/b/imperfectionist' },
  { id: 't6', days: 0, time: '4:32 pm', body: 'Three meetings, zero decisions. Is that a calendar problem or a me problem?' },
  { id: 't7', days: 0, time: '5:18 pm', body: '', title: 'How to end a workday when the work never ends', source_title: 'Harvard Business Review', source_url: 'https://hbr.org/2023/01/how-to-end-a-workday' },
  { id: 'y1', days: 1, time: '7:48 am', body: 'You do not rise to the level of your goals. You fall to the level of your systems.', source_title: 'James Clear, Atomic Habits', source_url: 'https://jamesclear.com/atomic-habits' },
  { id: 'y2', days: 1, time: '12:10 pm', body: 'Wrote for forty minutes with the phone in another room. It worked. Why is that still surprising?' },
  { id: 'y3', days: 1, time: '6:22 pm', body: '', title: 'The case for a shutdown ritual', source_title: 'calnewport.com', source_url: 'https://calnewport.com/drastically-reduce-stress-with-a-work-shutdown-ritual/' },
  { id: 'd1', days: 2, time: '9:05 am', body: 'Two unread books on the desk feel heavier than a hundred unread tabs.' },
  { id: 'd2', days: 2, time: '3:40 pm', body: 'The cost of a context switch is not the switch itself but the ten minutes after it.', source_title: 'Anne-Laure Le Cunff, Ness Labs', source_url: 'https://nesslabs.com/context-switching' },
  { id: 'w1', days: 4, time: '8:30 pm', body: 'Long walk, no podcast. Thought about nothing for twenty minutes and it felt like a holiday.' },
  { id: 't8', days: 15, time: '10:12 am', body: "There's a growing narrative that taste is the new superpower – the trait that separates originality from trend.", source_title: 'WTF is taste and why is it the new intelligence?', source_url: 'https://thechequebook.substack.com/p/wtf-is-taste' },
  { id: 't9', days: 17, time: '9:02 pm', body: "By its very nature, AI can't give you anything truly original. And that's where it's always going to fall short.", source_title: 'Your AI is showing · The Content Girl', source_url: 'https://thecontentgirl.substack.com/p/your-ai-is-showing' },
  { id: 't10', days: 23, time: '2:15 pm', body: 'Algorithms reward what is consumable, aka what is trending. Therefore algorithms are rewarding "AI Slop" because they are easy to consume.' },
  { id: 't11', days: 25, time: '11:40 am', body: 'Platforms have made it where taste has to be consumable to be seen. Edges have to be smoothed and palatable.' },
  { id: 't12', days: 31, time: '4:05 pm', body: '', title: 'Why The AI Giants Are Chasing Taste', source_title: 'wired.com', source_url: 'https://www.wired.com/story/ai-giants-chasing-taste/' },
]

export function demoThoughts(now: Date): Thought[] {
  return THOUGHTS.map((t) => ({
    id: t.id,
    title: t.title ?? null,
    body: t.body,
    source_url: t.source_url ?? null,
    source_title: t.source_title ?? null,
    image_path: t.photo ? DEMO_PHOTO_PATH : null,
    created_at: at(now, t.days, t.time),
  }))
}

export function demoCollections(): CollectionSummary[] {
  return [
    { id: 'c-taste', name: 'Taste & the machines', is_public: true, cover_color: '#D4848B', thought_count: 14, public_slug: 'taste-and-the-machines' },
    { id: 'c-attention', name: 'Attention', is_public: false, cover_color: '#8A7668', thought_count: 9 },
  ]
}

let n = 0
const id = () => `seed-${++n}`

function writing(now: Date, owner: string, w: Partial<Writing> & Pick<Writing, 'title' | 'blocks'>): Writing {
  const stamp = w.updated_at ?? now.toISOString()
  return {
    id: id(),
    owner_id: owner,
    kind: 'essay',
    subtitle: '',
    cover_url: null,
    cover_frame: null,
    show_times: true,
    show_sources: true,
    status: 'draft',
    visibility: 'public',
    slug: null,
    published_at: null,
    collection_id: null,
    share_collection: false,
    reflection_date: null,
    narration: null,
    word_count: countWords(w.title, w.blocks),
    created_at: stamp,
    ...w,
    updated_at: stamp,
  }
}

export function demoWritings(now: Date, owner: string): Writing[] {
  const hoursAgo = (h: number) => new Date(now.getTime() - h * 3_600_000).toISOString()
  const t = demoThoughts(now)
  const byId = (tid: string) => t.find((x) => x.id === tid)!

  // Composer v4 (design 4a/4b): every kept block type in place.
  const snap = (tid: string, kind: string) => {
    const x = byId(tid)
    return {
      text: x.body,
      ...(x.source_title || x.source_url ? { source: { title: x.source_title ?? undefined, url: x.source_url ?? undefined } } : {}),
      thoughtId: tid,
      capturedAt: x.created_at,
      kind,
    }
  }
  const taste: Block[] = [
    { id: id(), type: 'text', html: 'Can AI learn to have taste?' },
    { id: id(), type: 'image', src: DEMO_PHOTO_URL, caption: 'Chapter four, still', placement: 'wrap-right', thoughtId: 't4', capturedAt: byId('t4').created_at, kind: 'Photo' },
    { id: id(), type: 'text', html: 'Every platform, every scroll, every second—more inputs, more noise, more things trying to hook your attention. The old metrics of intelligence—who memorized the most, who spoke the loudest, who finished the book first—don’t mean much here.' },
    { id: id(), type: 'text', html: 'To appreciate, discern, and judge with authority, you need more than surface-level familiarity. The more deeply you understand the complexities, historical contexts, and subtle nuances within a field, the better your understanding becomes. True taste is stubbornly individualistic. It emerges quietly, gradually, often through personal choices that seem puzzling to others but make perfect sense internally.' },
    { id: id(), type: 'text', html: 'So, even if your taste is thoughtful, the platform pressures you to flatten it into something digestible – a moodboard, links, a top 10 list. It reduces something inherently nuanced into simplistic aesthetics that anyone can copy or paste.' },
    { id: id(), type: 'margin', ...snap('t11', 'Note'), own: true },
    { id: id(), type: 'voice', src: DEMO_VOICE_SRC, duration: 42, label: 'aside', transcript: 'I almost cut this paragraph. The moodboard line felt too easy, but it’s the one people quote back to me.', peaks: [0.35, 0.8, 0.55, 1, 0.45, 0.7, 0.9, 0.35, 0.6, 0.8, 0.5, 0.95, 0.4, 0.65, 0.85, 0.3, 0.55, 0.75, 0.45, 0.6], recordedAt: hoursAgo(30) },
    { id: id(), type: 'thought', ...snap('t9', 'Saved from thecontentgirl.substack.com') },
    { id: id(), type: 'heading', html: 'When does taste become trend?' },
    // A pinned margin note: it stays beside this heading and the next three paragraphs.
    { id: id(), type: 'margin', span: 3, ...snap('t9', 'Saved from thecontentgirl.substack.com') },
    { id: id(), type: 'text', html: 'But when everyone starts talking about taste, especially in the same aesthetic tone and on the same platforms, it stops functioning as a mark of discernment and starts reinforcing homogenisation.' },
    { id: id(), type: 'text', html: 'The way taste is being packaged – polished and made for virality – feels like it’s diluting the very thing it’s trying to defend.' },
    { id: id(), type: 'text', html: 'Algorithms reward what is consumable, aka what is trending. AI may not be capable of signalling what is taste and what is trend, but it can learn to follow what the masses are following.' },
    { id: id(), type: 'text', html: 'Taste as an idea is supposed to stand out against the boring trends, but once the tastemakers create a trend, does it then become <span data-unfold="udata">data AI can learn</span>? In an age where AI can generate anything, the question is no longer “can it be made?” but <span data-unfold="uworth">“is it worth making?”</span>' },
    { id: id(), type: 'unfold', anchor: 'udata', words: 'data AI can learn', ...snap('t10', 'Note'), own: true },
    { id: id(), type: 'unfold', anchor: 'uworth', words: '“is it worth making?”', ...snap('t8', 'Saved from thechequebook.substack.com') },
    { id: id(), type: 'scene', mode: 'solid', color: '#4A1525', height: 'half', label: 'Scene', html: 'The frontier isn’t volume. It’s discernment.' },
    { id: id(), type: 'text', html: 'And in that shift, taste has become a survival skill.' },
    { id: id(), type: 'pullquote', html: 'Taste isn’t a trait, it’s a skillset. And a skill that takes time, study, and precision is exactly what AI can learn.' },
    { id: id(), type: 'text', html: 'The question is whether the result is taste, or a very convincing average of it.' },
    { id: id(), type: 'text', html: '' },
  ]

  const reflection: Block[] = [
    { id: id(), type: 'text', html: 'I didn’t get much done today, or at least that’s how it felt. But looking back through what I saved, the day had a shape I couldn’t see while I was in it.' },
    { id: id(), type: 'thought', text: byId('t1').body, source: { title: byId('t1').source_title!, url: byId('t1').source_url! }, thoughtId: 't1', capturedAt: byId('t1').created_at, kind: 'Saved from theparisreview.org' },
    { id: id(), type: 'text', html: 'I thought about it for most of the morning. Not because it was new, but because it felt like an accusation.' },
    { id: id(), type: 'image', src: DEMO_PHOTO_URL, caption: '"Chapter four, still"', thoughtId: 't4', capturedAt: byId('t4').created_at, kind: 'Photo' },
    { id: id(), type: 'thought', text: byId('t5').body, source: { title: byId('t5').source_title!, url: byId('t5').source_url! }, thoughtId: 't5', capturedAt: byId('t5').created_at, kind: 'Saved from ckarchive.com' },
    { id: id(), type: 'text', html: 'That’s the one. The difficult thing today was the proposal draft, and every tab, every meeting, every saved quote was a small, polite way of not opening it.' },
    { id: id(), type: 'text', html: 'Tomorrow: open it first, before the train.' },
  ]

  const attention: Block[] = [
    { id: id(), type: 'text', html: 'I pay attention the way some people clean: in bursts, and mostly when someone is about to visit.' },
    { id: id(), type: 'text', html: 'The rest of the time it leaks. Into tabs, into the next notification, into the version of the conversation I’m rehearsing instead of having.' },
    { id: id(), type: 'divider' },
    { id: id(), type: 'text', html: 'Paying attention badly is still paying it. The trick is noticing where it went.' },
  ]

  return [
    writing(now, owner, {
      title: 'Can AI Learn to Have Taste?',
      subtitle: 'an exploration of how AI has a route to taste when the public argument says otherwise',
      blocks: taste,
      collection_id: 'c-taste',
      narration: { src: DEMO_NARRATION_SRC, duration: 372, recorded_at: hoursAgo(26), marks: {} },
      updated_at: hoursAgo(2),
    }),
    writing(now, owner, {
      kind: 'reflection',
      title: 'A day of small interruptions',
      subtitle: 'a reflection, stitched from what I saved along the way',
      blocks: reflection,
      status: 'published',
      slug: 'a-day-of-small-interruptions',
      published_at: hoursAgo(20),
      reflection_date: hoursAgo(20).slice(0, 10),
      updated_at: hoursAgo(20),
    }),
    writing(now, owner, {
      title: 'On paying attention badly',
      subtitle: '',
      blocks: attention,
      status: 'published',
      slug: 'on-paying-attention-badly',
      published_at: hoursAgo(24 * 18),
      updated_at: hoursAgo(24 * 18),
    }),
  ]
}
